import { Link } from "react-router";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { isOn } from "@/modules/apps/moduleDependencies";
import type { ModuleChange, ModuleRow } from "@/modules/apps/ModulesManager";
import {
  MODULES,
  type ModuleAutomation,
  type NomiModule,
} from "@/modules/registry";

type SavedAutomations = Record<string, { enabled?: unknown }>;

/** Saved value or the default for companies without one. */
export const automationChecked = (
  row: ModuleRow | undefined,
  automation: ModuleAutomation,
) => {
  const saved = (row?.options?.automations as SavedAutomations | undefined)?.[
    automation.key
  ]?.enabled;
  return typeof saved === "boolean" ? saved : automation.defaultOn;
};

/**
 * Switches for what an app does on its own (Apps page, platform console,
 * Settings → Automations). One that needs another app (texts → Messages)
 * stays locked until the company has it.
 */
export const ModuleAutomationSwitches = ({
  module,
  rows,
  busy,
  onChange,
  variant = "card",
}: {
  module: NomiModule;
  rows: ModuleRow[];
  busy?: boolean;
  onChange: (changes: ModuleChange[]) => void;
  /** card: Apps / Settings (links to the missing app); compact: console. */
  variant?: "card" | "compact";
}) => {
  const automations = module.automations ?? [];
  if (!automations.length) return null;
  const row = rows.find((r) => r.module_id === module.id);

  return (
    <div className="flex flex-col gap-2">
      {automations.map((automation) => {
        const missing = (automation.requires ?? []).filter(
          (id) => !isOn(rows, id),
        );
        const blocked = missing.length > 0;
        const checked = automationChecked(row, automation);
        return (
          <div
            key={automation.key}
            className={cn(
              "flex items-center justify-between gap-3",
              variant === "card"
                ? "rounded-xl border bg-card px-4 py-3"
                : "rounded-md bg-muted/50 px-3 py-2",
            )}
          >
            <div className="min-w-0">
              <p
                className={cn(
                  "text-sm font-medium",
                  blocked && "text-muted-foreground",
                )}
              >
                {automation.label}
              </p>
              <p className="text-xs text-muted-foreground">
                {automation.description}
              </p>
              {blocked ? (
                <p className="mt-1 text-xs text-warning-text">
                  Needs{" "}
                  {variant === "card" ? (
                    missing.map((id, index) => (
                      <span key={id}>
                        {index > 0 ? ", " : null}
                        <Link
                          to={`/apps/${id}`}
                          className="font-medium underline underline-offset-2"
                        >
                          {MODULES[id].name}
                        </Link>
                      </span>
                    ))
                  ) : (
                    <span className="font-medium">
                      {missing.map((id) => MODULES[id].name).join(", ")}
                    </span>
                  )}
                  . Turn it on first.
                </p>
              ) : null}
            </div>
            <Switch
              checked={checked && !blocked}
              disabled={busy || blocked}
              aria-label={`${automation.label} for ${module.name}`}
              onCheckedChange={(next) =>
                onChange([
                  {
                    moduleId: module.id,
                    enabled: true,
                    options: {
                      ...(row?.options ?? {}),
                      automations: {
                        ...((row?.options?.automations as
                          | SavedAutomations
                          | undefined) ?? {}),
                        [automation.key]: { enabled: next },
                      },
                    },
                  },
                ])
              }
            />
          </div>
        );
      })}
    </div>
  );
};
